import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DocsAPI } from '../lib/api';
import { MarkdownView } from '../components/MarkdownView';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { ChevronRight, ChevronDown, FileText, Folder } from 'lucide-react';

interface TreeNode {
  name: string;
  path: string;
  type: 'file' | 'dir';
  children?: TreeNode[];
}

function TreeItem({
  node,
  onSelect,
  selected,
  depth = 0,
}: {
  node: TreeNode;
  onSelect: (path: string) => void;
  selected: string | null;
  depth?: number;
}) {
  const [open, setOpen] = useState(depth === 0);

  if (node.type === 'dir') {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 w-full text-left px-2 py-1 text-sm text-tg-text-secondary hover:text-tg-text transition-colors"
          style={{ paddingLeft: `${8 + depth * 12}px` }}
        >
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Folder size={12} />
          <span>{node.name}</span>
        </button>
        {open && node.children?.map((child) => (
          <TreeItem key={child.path} node={child} onSelect={onSelect} selected={selected} depth={depth + 1} />
        ))}
      </div>
    );
  }

  return (
    <button
      onClick={() => onSelect(node.path)}
      className="flex items-center gap-1 w-full text-left px-2 py-1 text-sm transition-colors"
      style={{
        paddingLeft: `${8 + depth * 12}px`,
        color: selected === node.path ? 'var(--tg-accent)' : 'var(--tg-text)',
        background: selected === node.path ? 'var(--tg-bg-tertiary)' : 'transparent',
      }}
    >
      <FileText size={12} />
      <span>{node.name}</span>
    </button>
  );
}

export function DocsPage() {
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const { data: tree, isLoading: treeLoading } = useQuery({
    queryKey: ['docs-tree'],
    queryFn: DocsAPI.tree,
  });

  const { data: fileContent, isLoading: fileLoading } = useQuery({
    queryKey: ['doc', selectedPath],
    queryFn: () => DocsAPI.file(selectedPath!),
    enabled: !!selectedPath,
  });

  const nodes: TreeNode[] = Array.isArray(tree) ? tree : tree?.tree ?? [];
  const showContent = !!selectedPath;

  return (
    <div>
      <TopBar
        title="Документація"
        actions={
          showContent ? (
            <button
              onClick={() => setSelectedPath(null)}
              className="md:hidden text-sm text-tg-accent"
            >
              ← Список
            </button>
          ) : undefined
        }
      />
      <div className="flex h-[calc(100vh-56px)]">
        <aside
          className={`${showContent ? 'hidden md:block' : 'block'} w-full md:w-64 border-r border-tg-border overflow-y-auto py-2`}
          style={{ background: 'var(--tg-bg-secondary)' }}
        >
          {treeLoading ? (
            <div className="p-3 space-y-2">
              {[1,2,3].map(i => <Skeleton key={i} className="h-6 w-full" />)}
            </div>
          ) : nodes.length > 0 ? (
            nodes.map((node) => (
              <TreeItem
                key={node.path}
                node={node}
                onSelect={setSelectedPath}
                selected={selectedPath}
              />
            ))
          ) : (
            <p className="text-xs text-tg-text-secondary p-3">Файли не знайдені</p>
          )}
        </aside>

        <main className={`${showContent ? 'block' : 'hidden md:block'} flex-1 overflow-y-auto p-4 md:p-6`}>
          {!selectedPath ? (
            <div className="text-center py-16 text-tg-text-secondary">
              Оберіть файл зліва
            </div>
          ) : fileLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : fileContent ? (
            <MarkdownView content={typeof fileContent === 'string' ? fileContent : (fileContent as any).content ?? ''} />
          ) : (
            <p className="text-tg-text-secondary">Не вдалося завантажити файл</p>
          )}
        </main>
      </div>
    </div>
  );
}
